/**
 * @file DateRangeFilter.js
 * @version 1.1.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-09-23
 * @modified 2026-10-15
 *
 * The From / To / preset trio that every search screen filters a period with.
 *
 * Written once because it is three controls that only make sense together: the
 * two dates are the period, and the preset is a shortcut that writes both. A
 * screen that had only the dates made the user work out the first of last month
 * themselves; one that had only the preset could not answer "the eleventh to the
 * nineteenth". Invoice Detail Report had the pair working and every search
 * screen had a bare pair of date boxes, which is the drift this removes.
 *
 * Renders THREE sibling .filter-group cells rather than a wrapper, because the
 * parent's .filter-row is a grid and a wrapper would collapse them into one
 * column. Give the parent grid three columns for them — 92px 82px 26px is the
 * measured fit — and see DateRangeFilter.css for the negative margins that
 * close the gaps so the three read as one control.
 *
 * 1.1.0 (2026-10-15, Xero Reports): a screen may pass its own `presets`
 * ({ value, label, from?, to? } — a preset carrying its dates is taken as they
 * are, e.g. "Financial year to date" worked out by the server from Xero's year
 * end) and `presetFirst` with a `presetLabel`, which shows the preset as its
 * own labelled dropdown BEFORE the dates, reading "Last month" rather than an
 * arrow — for a report, where the period is the first thing anyone reads.
 * Without them every caller is exactly as before.
 */

import React from 'react';
import DateInput from './DateInput';
import Dropdown from './Dropdown';
import './DateRangeFilter.css';

/**
 * The shortcuts, in the order a period is usually asked for. 'custom' is what
 * the preset becomes the moment either date is typed — it is a label for
 * "these dates are yours", never something that resolves to a range.
 */
export const DATE_PRESETS = [
  { value: 'this-month', label: 'This Month' },
  { value: 'last-month', label: 'Last Month' },
  { value: 'this-quarter', label: 'This Quarter' },
  { value: 'last-quarter', label: 'Last Quarter' },
  { value: 'year-to-date', label: 'Year to Date' },
  { value: 'last-year', label: 'Last Year' },
  { value: 'today', label: 'Today' },
  { value: 'this-week', label: 'This Week' },
  { value: 'all-time', label: 'All Time' },
  { value: 'custom', label: 'Custom' }
];

const iso = (date) => {
  const yy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const dd = String(date.getDate()).padStart(2, '0');
  return `${yy}-${mm}-${dd}`;
};

/**
 * The (from, to) a preset means today. 'all-time' resolves to a pair of empty
 * strings — a search screen's period filter is optional, and clearing it is a
 * thing people want a one-click way to do.
 *
 * @param {string} preset
 * @returns {{from: string, to: string}} ISO dates, or '' for unbounded.
 */
export function resolveDateRange(preset) {
  const today = new Date();
  const y = today.getFullYear();
  const m = today.getMonth(); // 0-indexed
  const d = today.getDate();

  switch (preset) {
    case 'all-time':
      return { from: '', to: '' };
    case 'today':
      return { from: iso(today), to: iso(today) };
    case 'this-week': {
      // Monday → Sunday, the week containing today.
      const weekday = (today.getDay() + 6) % 7; // 0 = Monday
      return { from: iso(new Date(y, m, d - weekday)), to: iso(new Date(y, m, d - weekday + 6)) };
    }
    case 'last-month':
      return { from: iso(new Date(y, m - 1, 1)), to: iso(new Date(y, m, 0)) };
    case 'this-quarter': {
      const q = Math.floor(m / 3) * 3;
      return { from: iso(new Date(y, q, 1)), to: iso(new Date(y, q + 3, 0)) };
    }
    case 'last-quarter': {
      const q = Math.floor(m / 3) * 3 - 3;
      return { from: iso(new Date(y, q, 1)), to: iso(new Date(y, q + 3, 0)) };
    }
    case 'year-to-date':
      return { from: `${y}-01-01`, to: iso(today) };
    case 'last-year':
      return { from: `${y - 1}-01-01`, to: `${y - 1}-12-31` };
    case 'this-month':
    default:
      return { from: iso(new Date(y, m, 1)), to: iso(new Date(y, m + 1, 0)) };
  }
}

/**
 * @param {string}   from     ISO date, or '' for unbounded.
 * @param {string}   to       ISO date, or '' for unbounded.
 * @param {string}   preset   One of DATE_PRESETS' values.
 * @param {function} onChange ({ from, to, preset }) => void. Called with the
 *                   WHOLE trio every time, so a caller writes one state update
 *                   rather than three and cannot leave the preset saying
 *                   "This Month" over a range somebody has since edited.
 * @param {string}   [fromLabel='From']
 * @param {string}   [toLabel='To']
 */
function DateRangeFilter({
  from, to, preset = 'custom', onChange, fromLabel = 'From', toLabel = 'To',
  presets = null, presetFirst = false, presetLabel = 'Period'
}) {
  const options = presets || DATE_PRESETS;
  // Typing a date makes the period the user's own. Saying so beats silently
  // leaving a preset label that no longer describes what is on screen.
  const setDate = (field, value) => onChange({ from, to, [field]: value, preset: 'custom' });

  const setPreset = (value) => {
    if (value === 'custom') return onChange({ from, to, preset: 'custom' });
    const own = options.find(o => o.value === value);
    const range = own && own.from && own.to ? { from: own.from, to: own.to } : resolveDateRange(value);
    onChange({ ...range, preset: value });
  };

  const known = options.some(o => o.value === preset);
  const dropdownOptions = options.map(({ value, label }) => ({ value, label }));

  return (
    <>
      {presetFirst && (
        <div className="filter-group date-period-cell" title="Choose a period, or type the dates">
          <label>{presetLabel}</label>
          <Dropdown value={known ? preset : 'custom'} onChange={setPreset} options={dropdownOptions} />
        </div>
      )}
      <div className="filter-group date-from-cell">
        <DateInput label={fromLabel} value={from} onChange={(e) => setDate('from', e.target.value)} />
      </div>
      <div className="filter-group date-to-cell">
        <DateInput label={toLabel} value={to} onChange={(e) => setDate('to', e.target.value)} />
      </div>
      {!presetFirst && (
        <div className="filter-group date-preset-cell" title="Date range presets">
          <label>&nbsp;</label>
          <Dropdown value={preset} onChange={setPreset} options={dropdownOptions} />
        </div>
      )}
    </>
  );
}

export default DateRangeFilter;
